import supabase from '../supabase/supabase';

/* =========================================================
   SELECT
========================================================= */

const TRANSACTION_SELECT = `
  id,
  organization_id,
  account_id,
  source_account_id,
  batch_id,
  date,
  description,
  debit,
  credit,
  source,
  status,
  notes,
  created_by,
  created_at,
  updated_at,

  account:accounts!transactions_account_id_fkey (
    id,
    organization_id,
    code,
    name,
    type,
    sub_type,
    currency,
    is_active
  ),

  source_account:accounts!transactions_source_account_id_fkey (
    id,
    organization_id,
    code,
    name,
    type,
    sub_type,
    currency,
    is_active
  )
`;

/* =========================================================
   MAP TRANSACTION
========================================================= */

const mapTransaction = (transaction) => ({
  ...transaction,

  /*
   * account_id
   * = accounting category
   */
  account:
    transaction.account || null,

  /*
   * source_account_id
   * = bank/source account
   */
  source_account:
    transaction.source_account || null,

  /*
   * Backward compatibility.
   */
  coa_id:
    transaction.account_id,

  chart_of_accounts:
    transaction.account || null
});

/* =========================================================
   HELPERS
========================================================= */

const hasOwn = (
  object,
  field
) =>
  Object.prototype.hasOwnProperty.call(
    object,
    field
  );

const normalizeNumericValue = (
  value,
  field
) => {
  if (
    value === '' ||
    value === null ||
    value === undefined
  ) {
    return 0;
  }

  const numericValue =
    Number(value);

  if (!Number.isFinite(numericValue)) {
    throw new Error(
      `${field} must be a valid number.`
    );
  }

  if (numericValue < 0) {
    throw new Error(
      `${field} cannot be negative.`
    );
  }

  return numericValue;
};

/* =========================================================
   SERVICE
========================================================= */

export const transactionService = {

  /* =======================================================
     GET TRANSACTIONS
  ======================================================= */

  /**
   * Get posted General Ledger transactions.
   *
   * IMPORTANT:
   *
   * source_account_id
   *   = bank/source account
   *
   * account_id
   *   = accounting category
   */
  async getTransactions(
    filters = {},
    organizationId
  ) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    let query =
      supabase
        .schema('accounting')
        .from('transactions')
        .select(
          TRANSACTION_SELECT
        )
        .eq(
          'organization_id',
          organizationId
        )
        .order(
          'date',
          {
            ascending: false
          }
        )
        .order(
          'created_at',
          {
            ascending: false
          }
        );

    /*
     * Account filter means
     * SOURCE/BANK ACCOUNT.
     */
    if (
      filters?.accountId &&
      filters.accountId !== 'all'
    ) {
      query =
        query.eq(
          'source_account_id',
          filters.accountId
        );
    }

    /*
     * Status filter.
     */
    if (
      filters?.status &&
      filters.status !== 'All'
    ) {
      query =
        query.eq(
          'status',
          filters.status
        );
    }

    /*
     * Search.
     *
     * Keep the search scoped to
     * the current organization.
     */
    if (
      filters?.search?.trim()
    ) {
      const search =
        filters.search.trim();

      query =
        query.or(
          `description.ilike.%${search}%,source.ilike.%${search}%`
        );
    }

    const {
      data,
      error
    } =
      await query;

    if (error) {
      console.error(
        'getTransactions error:',
        error
      );

      throw error;
    }

    return (
      data || []
    ).map(
      mapTransaction
    );
  },

  /* =======================================================
     GET ONE TRANSACTION
  ======================================================= */

  /**
   * Get one transaction.
   *
   * Always scoped to the organization.
   */
  async getTransactionById(
    transactionId,
    organizationId
  ) {
    if (
      !transactionId ||
      !organizationId
    ) {
      throw new Error(
        'Transaction ID and organization ID are required.'
      );
    }

    const {
      data,
      error
    } =
      await supabase
        .schema('accounting')
        .from('transactions')
        .select(
          TRANSACTION_SELECT
        )
        .eq(
          'id',
          transactionId
        )
        .eq(
          'organization_id',
          organizationId
        )
        .maybeSingle();

    if (error) {
      console.error(
        'getTransactionById error:',
        error
      );

      throw error;
    }

    return data
      ? mapTransaction(data)
      : null;
  },

  /* =======================================================
     UPDATE TRANSACTION
  ======================================================= */

  /**
   * Update an existing General Ledger transaction.
   *
   * Supported fields:
   *
   * description
   * date
   * debit
   * credit
   * account_id
   * source_account_id
   * notes
   *
   * IMPORTANT:
   *
   * account_id
   *   = accounting category
   *
   * source_account_id
   *   = bank/source account
   *
   * notes
   *   = internal accounting memo / note
   *
   * No "amount" column is used.
   *
   * The transaction page/modal is responsible
   * for determining whether the amount belongs
   * to debit or credit.
   */
  async updateTransaction(
    transactionId,
    organizationId,
    updates = {}
  ) {
    if (
      !transactionId ||
      !organizationId
    ) {
      throw new Error(
        'Transaction ID and organization ID are required.'
      );
    }

    if (
      !updates ||
      typeof updates !== 'object'
    ) {
      throw new Error(
        'Transaction updates are required.'
      );
    }

    /* -----------------------------------------------------
       Allowed fields
    ----------------------------------------------------- */

    const allowedFields = [
      'description',
      'date',
      'debit',
      'credit',
      'account_id',
      'source_account_id',
      'notes'
    ];

    const payload = {};

    for (
      const field
      of allowedFields
    ) {
      if (
        hasOwn(
          updates,
          field
        )
      ) {
        payload[field] =
          updates[field];
      }
    }

    if (
      Object.keys(payload).length === 0
    ) {
      throw new Error(
        'No valid transaction fields were provided.'
      );
    }

    /* -----------------------------------------------------
       Description
    ----------------------------------------------------- */

    if (
      hasOwn(
        payload,
        'description'
      )
    ) {
      if (
        payload.description ===
          null ||
        payload.description ===
          undefined
      ) {
        payload.description = '';
      }

      if (
        typeof payload.description !==
        'string'
      ) {
        payload.description =
          String(
            payload.description
          );
      }

      payload.description =
        payload.description.trim();
    }

    /* -----------------------------------------------------
       Notes
    ----------------------------------------------------- */

    if (
      hasOwn(
        payload,
        'notes'
      )
    ) {
      if (
        payload.notes ===
          null ||
        payload.notes ===
          undefined
      ) {
        payload.notes = null;
      } else {
        if (
          typeof payload.notes !==
          'string'
        ) {
          payload.notes =
            String(
              payload.notes
            );
        }

        payload.notes =
          payload.notes.trim();

        /*
         * Store an empty note as null
         * rather than an empty string.
         */
        if (
          payload.notes === ''
        ) {
          payload.notes = null;
        }
      }
    }

    /* -----------------------------------------------------
       Date
    ----------------------------------------------------- */

    if (
      hasOwn(
        payload,
        'date'
      )
    ) {
      if (
        !payload.date
      ) {
        throw new Error(
          'Transaction date is required.'
        );
      }

      /*
       * Expected format:
       *
       * YYYY-MM-DD
       */
      const dateString =
        String(
          payload.date
        ).trim();

      const datePattern =
        /^\d{4}-\d{2}-\d{2}$/;

      if (
        !datePattern.test(
          dateString
        )
      ) {
        throw new Error(
          'Transaction date must use the YYYY-MM-DD format.'
        );
      }

      /*
       * Validate that the date
       * actually represents a real date.
       */
      const [
        year,
        month,
        day
      ] =
        dateString
          .split('-')
          .map(Number);

      const date =
        new Date(
          Date.UTC(
            year,
            month - 1,
            day
          )
        );

      if (
        date.getUTCFullYear() !==
          year ||
        date.getUTCMonth() !==
          month - 1 ||
        date.getUTCDate() !==
          day
      ) {
        throw new Error(
          'Transaction date is invalid.'
        );
      }

      payload.date =
        dateString;
    }

    /* -----------------------------------------------------
       Debit / Credit
    ----------------------------------------------------- */

    if (
      hasOwn(
        payload,
        'debit'
      )
    ) {
      payload.debit =
        normalizeNumericValue(
          payload.debit,
          'Debit'
        );
    }

    if (
      hasOwn(
        payload,
        'credit'
      )
    ) {
      payload.credit =
        normalizeNumericValue(
          payload.credit,
          'Credit'
        );
    }

    /*
     * If both sides are being updated,
     * prevent a transaction from having
     * both debit and credit simultaneously.
     *
     * Zero is allowed on either side.
     */
    if (
      hasOwn(
        payload,
        'debit'
      ) &&
      hasOwn(
        payload,
        'credit'
      )
    ) {
      if (
        payload.debit > 0 &&
        payload.credit > 0
      ) {
        throw new Error(
          'A transaction cannot have both a debit and a credit amount.'
        );
      }
    }

    /* -----------------------------------------------------
       Validate category account
    ----------------------------------------------------- */

    if (
      hasOwn(
        payload,
        'account_id'
      )
    ) {
      if (
        payload.account_id
      ) {
        const {
          data:
            categoryAccount,
          error:
            categoryError
        } =
          await supabase
            .schema(
              'accounting'
            )
            .from(
              'accounts'
            )
            .select(`
              id,
              organization_id,
              is_active
            `)
            .eq(
              'id',
              payload.account_id
            )
            .eq(
              'organization_id',
              organizationId
            )
            .maybeSingle();

        if (
          categoryError
        ) {
          console.error(
            'Category validation error:',
            categoryError
          );

          throw categoryError;
        }

        if (
          !categoryAccount
        ) {
          throw new Error(
            'The selected category does not belong to this organization.'
          );
        }

        if (
          !categoryAccount.is_active
        ) {
          throw new Error(
            'The selected category account is inactive.'
          );
        }
      }
    }

    /* -----------------------------------------------------
       Validate source account
    ----------------------------------------------------- */

    if (
      hasOwn(
        payload,
        'source_account_id'
      )
    ) {
      if (
        payload.source_account_id
      ) {
        const {
          data:
            sourceAccount,
          error:
            sourceError
        } =
          await supabase
            .schema(
              'accounting'
            )
            .from(
              'accounts'
            )
            .select(`
              id,
              organization_id,
              is_active
            `)
            .eq(
              'id',
              payload.source_account_id
            )
            .eq(
              'organization_id',
              organizationId
            )
            .maybeSingle();

        if (
          sourceError
        ) {
          console.error(
            'Source account validation error:',
            sourceError
          );

          throw sourceError;
        }

        if (
          !sourceAccount
        ) {
          throw new Error(
            'The selected source account does not belong to this organization.'
          );
        }

        if (
          !sourceAccount.is_active
        ) {
          throw new Error(
            'The selected source account is inactive.'
          );
        }
      }
    }

    /* -----------------------------------------------------
       Update transaction
    ----------------------------------------------------- */

    const {
      data,
      error
    } =
      await supabase
        .schema(
          'accounting'
        )
        .from(
          'transactions'
        )
        .update({
          ...payload,

          updated_at:
            new Date().toISOString()
        })
        .eq(
          'id',
          transactionId
        )
        .eq(
          'organization_id',
          organizationId
        )
        .select(
          TRANSACTION_SELECT
        )
        .maybeSingle();

    if (error) {
      console.error(
        'updateTransaction error:',
        error
      );

      throw error;
    }

    if (!data) {
      throw new Error(
        'Transaction was not found or could not be updated.'
      );
    }

    return mapTransaction(
      data
    );
  },

  /* =======================================================
     DELETE TRANSACTION
  ======================================================= */

  /**
   * Delete one transaction.
   *
   * Always scoped to the organization.
   */
  async deleteTransaction(
    transactionId,
    organizationId
  ) {
    if (
      !transactionId ||
      !organizationId
    ) {
      throw new Error(
        'Transaction ID and organization ID are required.'
      );
    }

    const {
      data,
      error
    } =
      await supabase
        .schema(
          'accounting'
        )
        .from(
          'transactions'
        )
        .delete()
        .eq(
          'id',
          transactionId
        )
        .eq(
          'organization_id',
          organizationId
        )
        .select(
          'id'
        )
        .maybeSingle();

    if (error) {
      console.error(
        'deleteTransaction error:',
        error
      );

      throw error;
    }

    if (!data) {
      throw new Error(
        'Transaction was not found or could not be deleted.'
      );
    }

    return data;
  }
};